import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const uid = params.get("uid");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (!token || !uid) { setError("Reset link is missing or invalid"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post(`/auth/reset-password?uid=${uid}`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "This reset link is invalid or has expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-12">
      <Seo title="Reset password · Jesus Just Us" path="/reset-password" noindex />
      <div className="w-full max-w-md jj-card p-8 sm:p-10 fade-up">
        <div className="overline text-burgundy">Reset password</div>
        <h1 className="font-serif text-4xl text-jjblue mt-2">Choose a new password</h1>

        {done ? (
          <div className="mt-8 text-center" data-testid="reset-done">
            <CheckCircle2 className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.4} />
            <p className="mt-4 text-jjblue">Your password has been updated.</p>
            <p className="text-sm text-[var(--jj-muted)] mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-jjblue/80">New password</label>
              <input className="input-jj mt-1" type="password" minLength={6} required
                value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">Confirm password</label>
              <input className="input-jj mt-1" type="password" minLength={6} required
                value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
            </div>
            {error && <div className="text-sm text-[var(--jj-burgundy)]" data-testid="reset-error">{error}</div>}
            <button disabled={busy} className="btn-primary w-full" data-testid="reset-submit">
              {busy ? "Updating…" : "Update password"}
            </button>
            <p className="text-sm text-[var(--jj-text-secondary)] text-center pt-2">
              <Link to="/login" className="text-burgundy font-medium">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
